import { useEffect, useState } from 'react'
import { ThreeDots } from 'react-loader-spinner'
import Lightbox from '../components/Lightbox'
import ImageSEO from '../components/ImageSEO'
import SEO from '../components/SEO'
import { GOOGLE_DRIVE_API_URL } from '../utils/api'
import './Strokes.css'

const STROKES_KEYWORDS =
  'Strokes, Strokes Sathish Paul SDB, Strokes by Sathish Paul SDB, Sathish Paul Strokes, Catholic stroke art, Christian line art, Catholic Art, Christian Art, Creative Artwork, Catholic Creative Resources, Sathish Paul SDB, Free Catholic Art, Christian Illustrations, Art Gallery, Stroke Art'

const STROKES_PRIMARY_IMAGE = '/images/Strokes/str1.jpg'
const STROKES_PRIMARY_IMAGE_URL = `https://www.sathishpaul.net${STROKES_PRIMARY_IMAGE}`
const STROKES_PRIMARY_IMAGE_ALT =
  'Strokes artwork by Sathish Paul SDB with orange and green brush strokes'

const STROKES_SEO_IMAGES = Array.from({ length: 27 }, (_, index) => ({
  src: `/images/Strokes/str${index + 1}.jpg`,
  name: `Strokes ${index + 1}`,
  description: `Strokes creative artwork ${index + 1} by Sathish Paul SDB`,
  textContent: `Strokes ${index + 1} by Sathish Paul SDB`
}))

const STROKES_API_URL = GOOGLE_DRIVE_API_URL

const Strokes = () => {
  const [strokeImages, setStrokeImages] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [lightboxData, setLightboxData] = useState({
    isOpen: false,
    src: '',
    caption: '',
    currentIndex: 0
  })

  useEffect(() => {
    if (!STROKES_API_URL) {
      setError('Google Drive API URL is not configured for Strokes.')
      setIsLoading(false)
      return
    }

    const callbackName = `strokesCallback_${Date.now()}`
    const script = document.createElement('script')
    const separator = STROKES_API_URL.includes('?') ? '&' : '?'
    let isActive = true

    setIsLoading(true)
    setError('')

    window[callbackName] = (data) => {
      if (!isActive) return

      console.log('Strokes data received:', data)

      const images = Array.isArray(data) ? data : []

      setStrokeImages(images)
      setIsLoading(false)
      setError('')
    }

    script.src = `${STROKES_API_URL}${separator}callback=${callbackName}`
    script.async = true
    script.onerror = () => {
      setIsLoading(false)
      setError('Sorry, the Strokes gallery could not be loaded right now.')
    }

    console.log('Strokes JSONP URL:', script.src)
    document.body.appendChild(script)

    return () => {
      isActive = false
      script.remove()
      setTimeout(() => {
        delete window[callbackName]
      }, 3000)
    }
  }, [])

  const openLightbox = (index) => {
    const image = strokeImages[index]
    if (!image) return

    setLightboxData({
      isOpen: true,
      src: image.imageUrl,
      caption: image.name || `Strokes ${index + 1}`,
      currentIndex: index
    })
  }

  const closeLightbox = () => {
    setLightboxData({ isOpen: false, src: '', caption: '', currentIndex: 0 })
  }

  const navigateLightbox = (direction) => {
    if (!strokeImages.length) return

    const nextIndex =
      direction === 'next'
        ? (lightboxData.currentIndex + 1) % strokeImages.length
        : (lightboxData.currentIndex - 1 + strokeImages.length) % strokeImages.length
    const image = strokeImages[nextIndex]

    setLightboxData({
      isOpen: true,
      src: image.imageUrl,
      caption: image.name || `Strokes ${nextIndex + 1}`,
      currentIndex: nextIndex
    })
  }

  const getFallbackUrl = (image) =>
    image.fallbackUrl || image.downloadUrl || image.userContentUrl || image.lh3Url || ''

  const strokesSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Strokes by Sathish Paul SDB',
    description:
      'Strokes creative artwork and Catholic visual resources by Sathish Paul SDB, featuring Catholic stroke art and Christian line illustrations.',
    url: 'https://www.sathishpaul.net/strokes',
    inLanguage: 'en',
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: STROKES_PRIMARY_IMAGE_URL,
      contentUrl: STROKES_PRIMARY_IMAGE_URL,
      name: 'Strokes by Sathish Paul SDB',
      description: STROKES_PRIMARY_IMAGE_ALT,
      width: 1080,
      height: 1350,
      caption: 'Strokes by Sathish Paul SDB',
      creator: {
        '@type': 'Person',
        name: 'Sathish Paul SDB'
      },
      copyrightHolder: {
        '@type': 'Person',
        name: 'Sathish Paul SDB'
      }
    },
    image: STROKES_PRIMARY_IMAGE_URL,
    creator: {
      '@type': 'Person',
      name: 'Sathish Paul SDB'
    },
    about: {
      '@type': 'Thing',
      name: 'Strokes'
    },
    keywords: STROKES_KEYWORDS
  }

  return (
    <main className="strokes-page">
      <SEO
        title="Strokes by Sathish Paul SDB | Catholic Stroke Art"
        description="Strokes by Sathish Paul SDB is a Catholic creative art gallery with Christian line art, stroke illustrations, and free visual resources."
        keywords={STROKES_KEYWORDS}
        image={STROKES_PRIMARY_IMAGE}
        imageAlt={STROKES_PRIMARY_IMAGE_ALT}
        imageWidth={1080}
        imageHeight={1350}
        schema={strokesSchema}
        canonical="https://www.sathishpaul.net/strokes"
      />
      <ImageSEO
        images={STROKES_SEO_IMAGES}
        pageUrl={typeof window !== 'undefined' ? window.location.href : 'https://www.sathishpaul.net/strokes'}
        pageTitle="Strokes by Sathish Paul SDB"
      />

      <section className="strokes-gallery" aria-label="Strokes artwork gallery">
        {isLoading && (
          <div className="strokes-loader">
            <ThreeDots
              height={60}
              width={60}
              color="#ffa72c"
              secondaryColor="#ffa72c"
              visible={true}
              ariaLabel="strokes-loading"
            />
          </div>
        )}
        {!isLoading && error && <p className="strokes-message strokes-message-error">{error}</p>}
        {!isLoading && !error && strokeImages.length === 0 && (
          <p className="strokes-message">No Strokes images are available yet.</p>
        )}
        {!isLoading &&
          !error &&
          strokeImages.map((image, index) => (
            <button
              type="button"
              className="strokes-card"
              key={image.id}
              onClick={() => openLightbox(index)}
            >
              <img
                src={image.imageUrl}
                alt={image.name}
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const img = e.currentTarget

                  if (!img.dataset.fallbackStep && image.thumbnailUrl) {
                    img.dataset.fallbackStep = '1'
                    img.src = image.thumbnailUrl
                    return
                  }

                  const fallbackUrl = getFallbackUrl(image)

                  if (img.dataset.fallbackStep === '1' && fallbackUrl) {
                    img.dataset.fallbackStep = '2'
                    img.src = fallbackUrl
                    return
                  }

                  img.style.display = 'none'
                }}
              />
            </button>
          ))}
      </section>

      <Lightbox
        isOpen={lightboxData.isOpen}
        src={lightboxData.src}
        caption={lightboxData.caption}
        onClose={closeLightbox}
        onNavigate={navigateLightbox}
        hasMultipleImages={strokeImages.length > 1}
        currentIndex={lightboxData.currentIndex}
        totalImages={strokeImages.length}
      />
    </main>
  )
}

export default Strokes
